import {
  ACTIVE_PROVIDER_SEARCH_VERSIONS,
  PROVIDER_SEARCH_VERSION_BAVARIA,
  PROVIDER_SEARCH_VERSION_WESER_EMS,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SERVICE_BASIC_CARE,
  SERVICE_BASIC_MEDICAL_CARE,
  SERVICE_CARE_FOR_CHILDREN_AND_TEENAGERS,
  SERVICE_CARE_FOR_YOUTH,
  SERVICE_CARE_PROTECTED_AREA,
  SERVICE_CLOSED_AREA,
  SERVICE_DEMENTIA,
  SERVICE_PALLIATIVE_CERTIFICATE,
  SERVICE_PSYCHIATRIC_CARE,
  SERVICE_SPECIALISED_INTENSIVE_NURSE,
  SERVICE_STATIC_CARE_DISABLED_PATIENTS,
} from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import {
  ActiveProviderSearchName,
  GetOntologyType,
  Patient,
  ProviderSearchPatient,
  SearchType,
} from "core/types";
import Translations from "translations/types";

export const PROVIDER_SEARCH_OVERWEIGHT = 130;

export const PSEUDO_SERVICE_OVERWEIGHT = -99;

export const PROVIDER_SEARCH_SERVICES = [
  SERVICE_BASIC_CARE,
  SERVICE_BASIC_MEDICAL_CARE,
  SERVICE_DEMENTIA,
  SERVICE_CARE_PROTECTED_AREA,
  SERVICE_CLOSED_AREA,
  SERVICE_PSYCHIATRIC_CARE,
  SERVICE_STATIC_CARE_DISABLED_PATIENTS,
  PSEUDO_SERVICE_OVERWEIGHT,
  SERVICE_SPECIALISED_INTENSIVE_NURSE,
  SERVICE_PALLIATIVE_CERTIFICATE,
  SERVICE_CARE_FOR_CHILDREN_AND_TEENAGERS,
  SERVICE_CARE_FOR_YOUTH,
];

export type ProviderSearchService = (typeof PROVIDER_SEARCH_SERVICES)[number];

export const getServiceItems = ({
  getOntology,
  services,
  translations,
}: {
  getOntology: GetOntologyType;
  services: number[];
  translations: Translations;
}): { id: number; label: string }[] => {
  return services.map((service) => {
    let label = getOntology({
      key: service,
      type: ONTOLOGIES.SERVICE,
    });

    if (service === PSEUDO_SERVICE_OVERWEIGHT) {
      label = translations.providersearch.assessment.overweightService({
        weight: PROVIDER_SEARCH_OVERWEIGHT.toString(),
      });
    }

    return {
      id: service,
      label,
    };
  });
};

export const patientToProviderSearchServices = ({
  patient,
  searchType,
}: {
  patient?: Patient;
  searchType?: SearchType;
}): ProviderSearchService[] => {
  const isPsychCare = searchType === SEARCH_TYPE_PSYCHIATRIC_CARE;

  const services = [
    patient?.diagnosis?.mental_health?.dementia_description != null
      ? SERVICE_DEMENTIA
      : null,
    patient?.care_needs?.basic_medical_care != null
      ? SERVICE_BASIC_MEDICAL_CARE
      : null,
    patient?.care_needs?.base_care?.description != null
      ? SERVICE_BASIC_CARE
      : null,
    isPsychCare && patient?.care_needs?.nutrition != null
      ? SERVICE_BASIC_CARE
      : null,
    isPsychCare && patient?.care_needs?.incontinence != null
      ? SERVICE_BASIC_CARE
      : null,
    isPsychCare && patient?.care_needs?.access_drainages != null
      ? SERVICE_BASIC_MEDICAL_CARE
      : null,
    isPsychCare && patient?.care_needs?.monitoring != null
      ? SERVICE_BASIC_MEDICAL_CARE
      : null,
    patient?.care_needs?.psychiatric_care != null
      ? SERVICE_PSYCHIATRIC_CARE
      : null,
    (patient?.profile?.weight_interval?.min ?? 0) >= PROVIDER_SEARCH_OVERWEIGHT
      ? PSEUDO_SERVICE_OVERWEIGHT
      : null,
    patient?.care_needs?.palliative?.palliative_certificate_needed
      ? SERVICE_PALLIATIVE_CERTIFICATE
      : null,
    patient?.care_needs?.breathing?.specialised_intensive_nurse
      ? SERVICE_SPECIALISED_INTENSIVE_NURSE
      : null,
    patient?.care_needs?.static_care_disabled_patients?.description != null
      ? SERVICE_STATIC_CARE_DISABLED_PATIENTS
      : null,
    patient?.diagnosis?.mental_health?.protected_area_needed != null
      ? SERVICE_CARE_PROTECTED_AREA
      : null,
    patient?.diagnosis?.mental_health?.run_away_description != null
      ? SERVICE_CLOSED_AREA
      : null,
    patient?.care_needs?.care_for_children_and_teenagers?.description != null
      ? SERVICE_CARE_FOR_CHILDREN_AND_TEENAGERS
      : null,
    patient?.care_needs?.care_for_youth?.description != null
      ? SERVICE_CARE_FOR_YOUTH
      : null,
  ].filter(Boolean) as ProviderSearchService[];

  // De-duplicate: base_care + nutrition would otherwise list SGB XI twice.
  return Array.from(new Set(services));
};

const hasService = (services: number[], service: number): boolean => {
  return services.includes(service);
};

export const servicesToProviderSearchPatient = (
  services: ProviderSearchService[],
): Partial<ProviderSearchPatient> => {
  return {
    care_needs: {
      base_care: hasService(services, SERVICE_BASIC_CARE)
        ? {
            description: "",
          }
        : undefined,
      basic_medical_care: hasService(services, SERVICE_BASIC_MEDICAL_CARE)
        ? {
            description: "",
          }
        : undefined,
      breathing: hasService(services, SERVICE_SPECIALISED_INTENSIVE_NURSE)
        ? {
            specialised_intensive_nurse: true,
          }
        : undefined,
      care_for_children_and_teenagers: hasService(
        services,
        SERVICE_CARE_FOR_CHILDREN_AND_TEENAGERS,
      )
        ? {
            description: "",
          }
        : undefined,
      care_for_youth: hasService(services, SERVICE_CARE_FOR_YOUTH)
        ? {
            description: "",
          }
        : undefined,
      palliative: hasService(services, SERVICE_PALLIATIVE_CERTIFICATE)
        ? {
            description: "",
            palliative_certificate_needed: true,
          }
        : undefined,
      psychiatric_care: hasService(services, SERVICE_PSYCHIATRIC_CARE)
        ? {
            description: "",
          }
        : undefined,
      static_care_disabled_patients: hasService(
        services,
        SERVICE_STATIC_CARE_DISABLED_PATIENTS,
      )
        ? {
            description: "",
          }
        : undefined,
    },
    diagnosis: {
      mental_health:
        hasService(services, SERVICE_DEMENTIA) ||
        hasService(services, SERVICE_CARE_PROTECTED_AREA) ||
        hasService(services, SERVICE_CLOSED_AREA)
          ? {
              ...(hasService(services, SERVICE_DEMENTIA) && {
                dementia_description: "",
              }),
              ...(hasService(services, SERVICE_CARE_PROTECTED_AREA) && {
                protected_area_needed: "",
              }),
              ...(hasService(services, SERVICE_CLOSED_AREA) && {
                run_away_description: "",
              }),
            }
          : undefined,
    },

    profile: hasService(services, PSEUDO_SERVICE_OVERWEIGHT)
      ? {
          weight_interval: {
            max: null,
            min: PROVIDER_SEARCH_OVERWEIGHT,
          },
        }
      : undefined,
  };
};

export const PROVIDER_SEARCH_LOCAL_STORAGE_KEY = "providersearch";

const getProviderSearchFromLocalStorage =
  (): ActiveProviderSearchName | null => {
    const providerSearch = localStorage.getItem(
      PROVIDER_SEARCH_LOCAL_STORAGE_KEY,
    );
    if (providerSearch) {
      return providerSearch as ActiveProviderSearchName;
    }
    return null;
  };

// - https://weser-ems.recaresolutions.com
// - https://weser-ems-preprod.recaresolutions.com
// - https://weser-ems-pr-name.staging.recaresolutions.com
const WESER_EMS_REGEX =
  /^https?:\/\/weser-ems(?:-[a-z0-9-]+)?(?:\.[a-z0-9-]+)*\.recaresolutions\.com$/i;

// - https://pflegefinder.bayern.recaresolutions.com
// - https://pflegefinder-bayern-preprod.recaresolutions.com
// - https://pflegefinder-bayern-pr-name.staging.recaresolutions.com
const BAVARIA_REGEX =
  /^https?:\/\/(?:pflegefinder\.bayern|pflegefinder-bayern(?:-[a-z0-9-]+)*)(?:\.[a-z0-9-]+)*\.recaresolutions\.com$/i;

export const getProviderSearchFromPathname =
  (): ActiveProviderSearchName | null => {
    const { origin, pathname } = window.location;

    if (WESER_EMS_REGEX.test(origin)) {
      return PROVIDER_SEARCH_VERSION_WESER_EMS;
    }

    if (BAVARIA_REGEX.test(origin)) {
      return PROVIDER_SEARCH_VERSION_BAVARIA;
    }

    const providerSearch = pathname.split("/")[2];

    if (
      !providerSearch ||
      !ACTIVE_PROVIDER_SEARCH_VERSIONS.includes(
        providerSearch as ActiveProviderSearchName,
      )
    ) {
      return getProviderSearchFromLocalStorage();
    }

    return providerSearch as ActiveProviderSearchName;
  };
